import { Colors } from "../../../utils/colors";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import StandardBorderedContainer from "../../Containers/StandardBorderedContainer";
import RectangleButton from "../../Buttons/RectangleButton";
import { useTheme } from "../../../hooks/useTheme";
import { Icon } from "@iconify/react";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import LabelButton from "../../Buttons/LabelButton";
import { convertToMoney, handlePluralString } from "@markit/common.utils";
import { MembershipPlan, NotificationType } from "@markit/common.types";
import {
  getNumMembershipSubscriptionMembers,
  getShortMembershipFrequencyLabel,
} from "../../../utils/subscriptionUtils";
import { DropdownMenu, DropdownMenuItem } from "../../DropdownMenu";
import ShareMembershipLinkModal from "../CreatorProfile/ShareMembershipLinkModal";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import AlertContainer from "../../Containers/AlertContainer";
import ConfirmDeleteModal from "../../Containers/ConfirmPopups/ConfirmDeleteModal";
import ConfirmActionModal from "../../Containers/ConfirmPopups/ConfirmActionModal";
import {
  changeMembershipPlan,
  deleteMembershipPlan,
} from "../../../utils/membershipUtils";
import ReadOnlyRichTextEditor from "../../TextFields/ReadOnlyRichTextEditor";
import DropdownArrowIcon from "../../DropdownsAndTabs/DropdownArrowIcon";

export type MembershipPlanItemProps = {
  membershipPlan: MembershipPlan;
  viewPlanSubscribers: (membershipId: string) => void;
  setCurrMembershipPlan: (membershipPlan: MembershipPlan) => void;
  dragIcon?: ReactNode;
};

const MembershipPlanItem = (props: MembershipPlanItemProps) => {
  const {
    membershipPlan,
    viewPlanSubscribers,
    setCurrMembershipPlan,
    dragIcon,
  } = props;
  const { theme } = useTheme();
  const { accountData } = useSelector(getAccountState).account;
  const dispatch = useDispatch();
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const [shareMembershipLink, setShareMembershipLink] = useState(false);

  const [deletePlanItem, setDeletePlanItem] = useState(false);
  const [confirmHidePlan, setConfirmHidePlan] = useState(false);
  const [activePlanAlertText, setActivePlanAlertText] = useState({
    header: "",
    subHeader: "",
  });
  const [deleteAlertText, setDeleteAlertText] = useState({
    header: "",
    subHeader: "",
  });

  const styles = {
    sectionContainer: {
      flex: 1,
      paddingBlock: 14,
      backgroundColor: Colors.WHITE,
    },
  };

  const confirmRemoveMembershipPlan = useCallback(async () => {
    if (membershipPlan.id === "free") {
      setDeleteAlertText({
        header: "You cannot delete the free plan.",
        subHeader:
          "If you'd like to not display the free plan, you can hide it instead.",
      });
      return;
    }

    const numPaidMembers = await getNumMembershipSubscriptionMembers(
      accountData.uid,
      membershipPlan.id
    );

    // if there are paid members, don't allow deletion
    if (numPaidMembers > 0) {
      setDeleteAlertText({
        header: "You currently have paid members on this plan.",
        subHeader:
          "You can only delete the plan if there are no paid members for the specific plan.",
      });
      return;
    }
    setDeletePlanItem(true);
  }, [accountData.uid, membershipPlan.id]);

  const removeMembershipPlan = useCallback(async () => {
    await deleteMembershipPlan(membershipPlan.id, accountData.uid);
    showNotificationBanner(dispatch, "Plan Deleted", NotificationType.NEGATIVE);
  }, [accountData.uid, membershipPlan.id, dispatch]);

  const confirmHideMembershipPlan = useCallback(async () => {
    // if this is the last visible plan, don't allow hiding
    const visibleMembershipPlans =
      accountData.membership.membershipPlans.filter(
        (plan) => !plan.hidden || plan.id === membershipPlan.id
      );
    if (visibleMembershipPlans.length <= 1 && !membershipPlan.hidden) {
      setActivePlanAlertText({
        header: "You must have at least one active plan",
        subHeader:
          "You can't hide all membership plans. If you would like to not show memberships you must turn off the memberships toggle.",
      });
    } else {
      setConfirmHidePlan(true);
    }
  }, [
    accountData.membership.membershipPlans,
    membershipPlan.hidden,
    membershipPlan.id,
  ]);

  const hideMembershipPlan = useCallback(async () => {
    const updatedPlan = {
      ...membershipPlan,
      hidden: !membershipPlan.hidden,
    };
    await changeMembershipPlan(updatedPlan, accountData.uid);
    showNotificationBanner(
      dispatch,
      updatedPlan.hidden ? "Plan Hidden" : "Plan Activated",
      NotificationType.AFFIRMATIVE
    );
  }, [accountData.uid, membershipPlan, dispatch]);

  const dropdownItems: DropdownMenuItem[] = useMemo(() => {
    const items: DropdownMenuItem[] = [
      {
        title: "Edit Plan",
        icon: "ion:create",
        key: "edit_plan",
        onPress: () => setCurrMembershipPlan(membershipPlan),
      },
      {
        title: "View Subscribers",
        icon: "ion:people",
        key: "view_subscribers",
        onPress: () => viewPlanSubscribers(membershipPlan.id),
      },
      {
        title: membershipPlan.hidden ? "Activate Plan" : "Hide Plan",
        icon: membershipPlan.hidden ? "ion:eye" : "ion:eye-off",
        key: "hide_plan",
        onPress: confirmHideMembershipPlan,
        labelColor: membershipPlan.hidden ? Colors.GREEN2 : Colors.ORANGE1,
      },
      {
        title: "Delete Plan",
        icon: "ion:trash",
        key: "delete_plan",
        onPress: confirmRemoveMembershipPlan,
        isDestructive: true,
      },
    ];

    return items;
  }, [
    membershipPlan,
    confirmHideMembershipPlan,
    confirmRemoveMembershipPlan,
    setCurrMembershipPlan,
    viewPlanSubscribers,
  ]);

  return (
    <>
      <StandardBorderedContainer
        key={membershipPlan.id}
        containerStyles={styles.sectionContainer}
      >
        <div
          className="AlignedRowSpaced"
          style={{ alignItems: "flex-start", paddingInline: 14 }}
        >
          <div className="ColumnNormal" style={{ gap: 7 }}>
            <LabelButton
              color={membershipPlan.hidden ? Colors.ORANGE1 : Colors.GREEN2}
              backgroundColor={
                membershipPlan.hidden
                  ? `${Colors.ORANGE1}1A`
                  : `${Colors.GREEN2}1A`
              }
              borderColor={
                membershipPlan.hidden ? Colors.ORANGE1 : Colors.GREEN2
              }
              text={membershipPlan.hidden ? "Hidden" : "Active"}
            />
            <LabelButton
              text={membershipPlan.name}
              color={Colors.BLACK}
              backgroundColor={Colors.GRAY6}
              icon={
                membershipPlan.groupMembers ? (
                  <Icon icon="ion:people-circle" height={16} />
                ) : undefined
              }
              subtext={
                membershipPlan.groupMembers
                  ? `${membershipPlan.groupMembers} ${handlePluralString(
                      "Additional Member",
                      membershipPlan.groupMembers
                    )}`
                  : undefined
              }
            />
            <div
              className="AlignedRow"
              style={{ gap: 4, alignItems: "baseline" }}
            >
              <span style={{ fontSize: 24 }}>
                {convertToMoney(membershipPlan.price / 100)}
              </span>
              {membershipPlan.id !== "free" ? (
                <span className="smallBodySubtext">
                  /
                  {getShortMembershipFrequencyLabel(
                    membershipPlan.billingFrequency
                  )}
                </span>
              ) : null}
            </div>
          </div>
          <div className="AlignedRow" style={{ gap: 10 }}>
            <DropdownMenu
              dropdownItems={dropdownItems}
              placement="bottomRight"
              trigger={["click"]}
              altWidth={200}
              isOpen={isOpenDropdown}
              onOpenChange={(isOpen: boolean) => setIsOpenDropdown(isOpen)}
            >
              <RectangleButton
                buttonLabel="Manage"
                onPress={() => {}}
                altTextColor={Colors.BLACK}
                containerStyles={{
                  whiteSpace: "nowrap",
                  padding: 14,
                  backgroundColor: Colors.WHITE1,
                  border: `0.5px solid ${Colors.GRAY2}`,
                }}
                iconRight={
                  <DropdownArrowIcon
                    isOpen={isOpenDropdown}
                    color={Colors.BLACK}
                    height={14}
                  />
                }
              />
            </DropdownMenu>
            <RectangleButton
              buttonLabel="Share"
              onPress={() => setShareMembershipLink(true)}
              iconRight={
                <Icon
                  icon={"ion:share"}
                  height={14}
                  style={{ color: Colors.WHITE }}
                />
              }
              altColor={Colors.BLACK}
              altTextColor={Colors.WHITE}
              containerStyles={{ padding: 14 }}
            />
            {dragIcon || null}
          </div>
        </div>
        <HorizontalDivider altMargin={14} />
        <div className="ColumnNormal" style={{ gap: 7, paddingInline: 14 }}>
          <span className="bodyMedium" style={{ fontSize: 12 }}>
            Benefits
          </span>
          <ReadOnlyRichTextEditor
            body={membershipPlan.body}
            theme={theme}
            className={"post-content-editor"}
          />
        </div>
      </StandardBorderedContainer>
      {shareMembershipLink ? (
        <ShareMembershipLinkModal
          username={accountData.username}
          setShareLinkModal={setShareMembershipLink}
        />
      ) : null}
      <AlertContainer
        headerComp={
          <div className="ColumnNormal" style={{ gap: 4 }}>
            <Icon icon="ion:trash" height={35} style={{ color: Colors.RED3 }} />
            <span style={{ fontSize: 16, fontWeight: "500" }}>
              {deleteAlertText.header}
            </span>
          </div>
        }
        subHeaderComp={deleteAlertText.subHeader}
        closeModal={() => setDeleteAlertText({ header: "", subHeader: "" })}
        hideModal={deleteAlertText.header === ""}
      />
      <ConfirmActionModal
        heading={membershipPlan.hidden ? "Activate Plan?" : "Hide Plan?"}
        subtext={
          membershipPlan.hidden
            ? "Are you sure you want to activate this membership plan? Activated plans will be displayed when people subscribe."
            : "Are you sure you want to hide this membership plan? Hidden plans will not be displayed when people subscribe. Subscribers on hidden plans will still be able to view the plan."
        }
        confirmButtonText={
          membershipPlan.hidden ? "Activate Plan" : "Hide Plan"
        }
        icon={
          <Icon
            icon={membershipPlan.hidden ? "ion:eye" : "ion:eye-off"}
            height={35}
            style={{
              color: membershipPlan.hidden ? Colors.GREEN2 : Colors.ORANGE1,
            }}
          />
        }
        hideModal={!confirmHidePlan}
        setIsVisible={setConfirmHidePlan}
        confirmOnPress={hideMembershipPlan}
        altConfirmBtnColor={
          membershipPlan.hidden ? Colors.GREEN2 : Colors.ORANGE1
        }
      />
      <AlertContainer
        headerComp={
          <div className="ColumnNormal" style={{ gap: 4 }}>
            <Icon
              icon="ion:eye-off"
              height={35}
              style={{ color: Colors.ORANGE1 }}
            />
            <span style={{ fontSize: 16, fontWeight: "500" }}>
              {activePlanAlertText.header}
            </span>
          </div>
        }
        subHeaderComp={activePlanAlertText.subHeader}
        closeModal={() => setActivePlanAlertText({ header: "", subHeader: "" })}
        hideModal={activePlanAlertText.header === ""}
        altButtonLabel="Got it"
      />
      <ConfirmDeleteModal
        heading="Delete Membership Plan?"
        subtext="Are you sure you want to delete this membership plan? This cannot be undone."
        deleteButtonText="Delete Plan"
        hideModal={!deletePlanItem}
        setIsVisible={(visible: boolean) => setDeletePlanItem(visible)}
        deleteOnPress={removeMembershipPlan}
        icon={
          <Icon icon="ion:trash" height={35} style={{ color: Colors.RED3 }} />
        }
        theme={theme}
      />
    </>
  );
};

export default MembershipPlanItem;
